import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';

import MillesimaLink from '@/components/common/MillesimaLink';
import { NS } from '@/constants';
import { formatUrl, getHrefForCategLink } from '@/utils/linkUtils';

import styles from './ProductCardAttributes.module.scss';
import { TYPE_PRODUIT } from '@/constants/product';

const ProductCardAttributes = ({
  index,
  classement,
  region,
  appellation,
  typeProduit,
  sousTypeProduit,
  mentionAge,
  matierePremiere,
  origineSpiritueux,
  couleur,
  customClassName = '',
  scrollToAttributes,
  storeConf,
  isSpirit,
  producer,
  isCULayout = false
}) => {
  const { t } = useTranslation(NS.COMMON);

  const attributesList = useMemo(() => getAttributesList(), [
    classement,
    region,
    appellation,
    typeProduit,
    sousTypeProduit,
    mentionAge,
    matierePremiere,
    origineSpiritueux,
    couleur,
    isSpirit,
    producer,
    isCULayout,
  ]);

  const getLink = (categName, { url, value, valueId, isSpirit, groupIdentifier }, key, notLastElement) => (
    <React.Fragment key={`${valueId}-${key}`}>
      <MillesimaLink
        type="inline-link"
        id={`product${index}-goTo${categName}Listing`}
        href={getHrefForCategLink(url, groupIdentifier, isSpirit)}
        as={formatUrl(url, isSpirit, storeConf)}
        ariaLabel={value}
        innerHtml={value}
        customClassName={{ link: customClassName }}
      />
      {`${notLastElement ? ' - ' : ''}`}
    </React.Fragment>
  );

  function getAttributesList() {
    const attributes = [];
    if (!isSpirit) {
      if (classement) {
        attributes.push({ type: 'text', value: classement });
      }
      if (region?.url) {
        attributes.push({ type: 'link', value: region, categName: 'Region' });
      }
      if (appellation?.url) {
        attributes.push({ type: 'link', value: appellation, categName: 'Appellation' });
      }
      if (couleur && !isCULayout) {
        attributes.push({ type: 'text', value: couleur });
      }
      if (producer?.url) {
        attributes.push({ type: 'link', value: producer, categName: 'Producteur' });
      }
    } else {
      switch (typeProduit?.valueId) {
        case TYPE_PRODUIT.WHISKY:
          if (typeProduit?.url) {
            attributes.push({ type: 'link', value: typeProduit, categName: 'TypeProduit' });
          }
          if (origineSpiritueux?.url) {
            attributes.push({ type: 'link', value: origineSpiritueux, categName: 'OrigineSpiritueux' });
          }
          if (sousTypeProduit?.url) {
            attributes.push({ type: 'link', value: sousTypeProduit, categName: 'SousTypeProduit' });
          }
          break;
        case TYPE_PRODUIT.COGNAC:
        case TYPE_PRODUIT.ARMAGNAC:
          if (typeProduit?.url) {
            attributes.push({ type: 'link', value: typeProduit, categName: 'TypeProduit' });
          }
          if (appellation?.url) {
            attributes.push({ type: 'link', value: appellation, categName: 'Appellation' });
          }
          if (mentionAge?.url) {
            attributes.push({ type: 'link', value: mentionAge, categName: 'MentionAge' });
          }
          break;
        case TYPE_PRODUIT.GIN:
          if (typeProduit?.url) {
            attributes.push({ type: 'link', value: typeProduit, categName: 'TypeProduit' });
          }
          if (sousTypeProduit?.url) {
            attributes.push({ type: 'link', value: sousTypeProduit, categName: 'SousTypeProduit' });
          }
          if (origineSpiritueux?.url) {
            attributes.push({ type: 'link', value: origineSpiritueux, categName: 'OrigineSpiritueux' });
          }
          break;
        case TYPE_PRODUIT.RHUM:
          if (typeProduit?.url) {
            attributes.push({ type: 'link', value: typeProduit, categName: 'TypeProduit' });
          }
          if (origineSpiritueux?.url) {
            attributes.push({ type: 'link', value: origineSpiritueux, categName: 'OrigineSpiritueux' });
          }
          if (mentionAge?.url) {
            attributes.push({ type: 'link', value: mentionAge, categName: 'MentionAge' });
          }
          if (couleur) {
            attributes.push({ type: 'text', value: couleur });
          }
          break;
        case TYPE_PRODUIT.TEQUILA:
          if (typeProduit?.url) {
            attributes.push({ type: 'link', value: typeProduit, categName: 'TypeProduit' });
          }
          if (mentionAge?.url) {
            attributes.push({ type: 'link', value: mentionAge, categName: 'MentionAge' });
          }
          break;
        case TYPE_PRODUIT.VODKA:
          if (typeProduit?.url) {
            attributes.push({ type: 'link', value: typeProduit, categName: 'TypeProduit' });
          }
          if (origineSpiritueux?.url) {
            attributes.push({ type: 'link', value: origineSpiritueux, categName: 'OrigineSpiritueux' });
          }
          break;
        case TYPE_PRODUIT.EAUX_DE_VIE:
          if (typeProduit?.url) {
            attributes.push({ type: 'link', value: typeProduit, categName: 'TypeProduit' });
          }
          if (sousTypeProduit?.url) {
            attributes.push({ type: 'link', value: sousTypeProduit, categName: 'SousTypeProduit' });
          }
          if (matierePremiere?.url) {
            attributes.push({ type: 'link', value: matierePremiere, categName: 'MatierePremiere' });
          }
          break;
        default:
          if (typeProduit?.url) {
            attributes.push({ type: 'link', value: typeProduit, categName: 'TypeProduit' });
          }
      }
    }

    return attributes;
  }

  return attributesList.length > 0 ? (
    <div className={`${styles.container} ${customClassName}`} data-test-id="product-card-attributes">
      {attributesList.map((attribute, key) => {
        if (attribute.type === 'text') {
          return <span key={`${attribute.value}-${key}`}>{attribute.value} {key < attributesList.length - 1 ? ' - ' : ''}</span>;
        } else if (attribute.type === 'link') {
          return getLink(attribute.categName, attribute.value, key, key < attributesList.length - 1);
        }
      })}

      {scrollToAttributes && (
        <>
          {attributesList.length > 0 && ' - '}
          <span className={styles['details-action']} onClick={scrollToAttributes}>
            {t('product.details.plural')}
          </span>
        </>
      )}
    </div>
  ) : null;
};

const attributeShape = {
  value: PropTypes.string,
  valueId: PropTypes.string,
  url: PropTypes.string
};

ProductCardAttributes.propTypes = {
  index: PropTypes.number,
  classement: PropTypes.string,
  region: PropTypes.shape(attributeShape),
  appellation: PropTypes.shape(attributeShape),
  typeProduit: PropTypes.shape(attributeShape),
  sousTypeProduit: PropTypes.shape(attributeShape),
  origineSpiritueux: PropTypes.shape(attributeShape),
  matierePremiere: PropTypes.shape(attributeShape),
  mentionAge: PropTypes.shape(attributeShape),
  couleur: PropTypes.string,
  customClassName: PropTypes.string,
  scrollToAttributes: PropTypes.func,
  storeConf: PropTypes.object,
  isSpirit: PropTypes.bool
};

export default ProductCardAttributes;
